import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
} from "@xyflow/react"

import type { Segment } from "../types"
import type { CompanyDisplayRole, ConnectionRole } from "../utils/segment-connections"
import type { Node, NodeProps, NodeTypes } from "@xyflow/react"
import type { SimulationLinkDatum } from "d3-force"

import { Maximize2, Minimize2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { ConnectionsGraphLegend } from "./connections-graph-legend"
import { COMPANY_NAME } from "../utils/branding"
import {
  computeLayout,
  createSimulation,
  type LiveSimulation,
  marketBubbleFrame,
  prefersReducedMotion,
  pruneMarketFilter,
  type SimNode,
} from "../utils/connections-graph-layout"
import { haloTextShadow, readableTextOn } from "../utils/readable-text"
import { COMPANY_FILL, companyDisplayRole } from "../utils/segment-connections"

import "@xyflow/react/dist/style.css"

type ConnectionsGraphProps = {
  segments: Segment[]
  selectedSegmentId: string | null
  onSelectSegment: (segmentId: string) => void
  onOpenCompany: (name: string, segmentId: string) => void
}

type BubbleData = {
  label: string
  /** Circle diameter in px (node width/height equal this). */
  size: number
  color: string
  /** Softer fill derived from `color`; the border carries the full color. */
  kind: "market" | "segment" | "company"
  roles?: ConnectionRole[]
  sales?: number
  selected: boolean
  dimmed: boolean
}

// Keep the initial fit readable: never zoom out past 0.35, so a large portfolio's
// nodes stay legible/clickable (users pan, or scroll out for the full overview)
// while small portfolios still fit fully. Never zoom in past 1.1 for a tiny graph.
const FIT_OPTIONS = { padding: 0.15, minZoom: 0.35, maxZoom: 1.1 }

/**
 * One circular node for every kind — a filled bubble with the label inside,
 * neo4j-style. Centered invisible handles make edges run centre-to-centre so
 * straight links look like a true force-directed network rather than a flow.
 */
function BubbleNode({ data }: NodeProps<Node<BubbleData, "bubble">>) {
  const { size, color, kind, label, selected, dimmed } = data
  let labelColor = "hsl(var(--primary-foreground))"
  let labelHalo: string | undefined
  if (color === COMPANY_FILL.competitor) labelColor = "hsl(var(--background))"
  else if (kind !== "company") {
    labelColor = readableTextOn(color)
    // Mid-tone market fills can't reach 4.5:1 with either ink; add an opposite-colour halo.
    labelHalo = haloTextShadow(color)
  }
  const fontSize = FONT_SIZE[kind]
  // Market bubbles carry the same colour family as their segments but need to
  // read as a different KIND of node, not just a bigger one (#3306 #14): a
  // bolder label plus a double-ring frame instead of the plain shadow.
  // Markets are never the selected node (only `segment:*` ids can be), so the
  // frame never has to compete with the selection ring below.
  const isMarket = kind === "market"
  // Styled fast tooltip (#2073 M-3) — truncated labels reveal instantly instead
  // of waiting on the browser's native title delay.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex cursor-pointer items-center justify-center rounded-full text-center shadow-sm transition-opacity duration-150",
            isMarket ? "font-bold" : "font-semibold",
            selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
            dimmed && "opacity-20"
          )}
          style={{
            width: size,
            height: size,
            background: color,
            // Competitor bubbles are filled with --muted-foreground (dark in light mode,
            // light in dark mode): label flips to --background. Other fills are pale.
            color: labelColor,
            textShadow: labelHalo,
            fontSize,
            lineHeight: 1.1,
            ...(isMarket ? marketBubbleFrame(color) : undefined),
          }}
        >
          <span className="pointer-events-none overflow-hidden px-1.5 break-words">
            {truncateLabel(label, size)}
          </span>
          <Handle position={Position.Top} style={centerHandleStyle} type="target" />
          <Handle position={Position.Bottom} style={centerHandleStyle} type="source" />
        </div>
      </TooltipTrigger>
      <TooltipContent className="text-xs" sideOffset={6}>
        {data.sales != null ? `${label} — ${data.sales} MEUR ${COMPANY_NAME}` : label}
      </TooltipContent>
    </Tooltip>
  )
}

// Park both handles dead-centre (and invisible) so straight edges connect the
// circle centres; the circle itself occludes the line so links appear to stop
// cleanly at each bubble's rim.
const centerHandleStyle: React.CSSProperties = {
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
  border: "none",
  background: "transparent",
  pointerEvents: "none",
  opacity: 0,
}

// Rough fit: ~2 lines of characters inside a circle of the given diameter.
const LABEL_FIT: Array<[minSize: number, maxChars: number]> = [
  [80, 22],
  [60, 16],
  [40, 12],
  [0, 9],
]

function truncateLabel(label: string, size: number): string {
  const max = LABEL_FIT.find(([minSize]) => size >= minSize)?.[1] ?? 9
  return label.length > max ? `${label.slice(0, max - 1)}…` : label
}

const FONT_SIZE: Record<BubbleData["kind"], number> = { market: 12, segment: 11, company: 9 }

function edgeOpacity(lit: boolean, hovering: boolean): number {
  if (!lit) return 0.12
  return hovering ? 0.95 : 0.75
}

const nodeTypes: NodeTypes = {
  bubble: BubbleNode,
}

function ConnectionsGraphInner({
  segments,
  selectedSegmentId,
  onSelectSegment,
  onOpenCompany,
}: ConnectionsGraphProps) {
  // Map filters (#2071). Market filter is DATA-level: it narrows the segments
  // the layout is computed from, so the graph re-lays-out (and re-fits) around
  // the remaining markets — the elastic sim rebuilds with them. Role filter is
  // VISUAL: company nodes/edges hide in place, so toggling roles never shuffles
  // the layout under the cursor.
  const [hiddenRoles, setHiddenRoles] = useState<Set<CompanyDisplayRole>>(new Set())
  const [marketFilter, setMarketFilter] = useState<Set<string>>(new Set())
  const markets = useMemo(
    () => Array.from(new Set(segments.map((segment) => segment.marketName))),
    [segments]
  )
  // The legend's market filter is local state, but `segments` can narrow out from
  // under it — e.g. the global Market chip changes while the Map stays mounted
  // (#3306 #7 review finding 1). A stale selection (a market no longer present)
  // must not silently zero out the graph, so every read of the filter goes
  // through this pruned view instead of the raw state.
  const effectiveMarketFilter = useMemo(
    () => pruneMarketFilter(marketFilter, markets),
    [marketFilter, markets]
  )
  // Actually prune the raw state too (#3306 #7 review finding 4) — otherwise a
  // stale isolated market (e.g. Mobility) survives underneath the masked
  // `effectiveMarketFilter` and snaps back the instant `markets` widens again
  // (the outer Market chip narrowing, then clearing). `pruneMarketFilter`
  // returns the SAME Set reference when nothing changed, so this setState is a
  // no-op then and can't loop.
  useEffect(() => {
    setMarketFilter((current) => pruneMarketFilter(current, markets))
  }, [markets])
  const scopedSegments = useMemo(
    () =>
      effectiveMarketFilter.size > 0
        ? segments.filter((segment) => effectiveMarketFilter.has(segment.marketName))
        : segments,
    [segments, effectiveMarketFilter]
  )

  const layout = useMemo(() => computeLayout(scopedSegments), [scopedSegments])
  const [nodes, setNodes, onNodesChange] = useNodesState(layout.nodes)
  const [hovered, setHovered] = useState<string | null>(null)

  // Company node ids hidden by the role toggles (and their edges below).
  const hiddenCompanyIds = useMemo(() => {
    if (hiddenRoles.size === 0) return new Set<string>()
    const ids = new Set<string>()
    for (const node of layout.nodes) {
      const data = node.data as BubbleData
      if (
        data.kind === "company" &&
        data.roles &&
        hiddenRoles.has(companyDisplayRole(data.roles))
      ) {
        ids.add(node.id)
      }
    }
    return ids
  }, [layout.nodes, hiddenRoles])

  const toggleRole = useCallback((role: CompanyDisplayRole) => {
    setHiddenRoles((current) => {
      const next = new Set(current)
      if (next.has(role)) next.delete(role)
      else next.add(role)
      return next
    })
  }, [])

  // Market chips: empty set = all markets. First click isolates that market;
  // unticking the last active market returns to "all".
  const toggleMarket = useCallback((marketName: string) => {
    setMarketFilter((current) => {
      const next = new Set(current)
      if (next.has(marketName)) next.delete(marketName)
      else next.add(marketName)
      return next
    })
  }, [])
  const { fitView } = useReactFlow()
  const fitViewRef = useRef(fitView)
  fitViewRef.current = fitView

  useEffect(() => setNodes(layout.nodes), [layout, setNodes])

  // Live elastic simulation: the same forces as the initial layout, kept around
  // (cooled to alpha 0) so dragging a node can reheat them — the spring links
  // tug the neighbours along mid-drag and the graph wobbles back to rest on
  // release. The tick handler is the single writer: the dragged node's React
  // Flow position feeds the sim pin (fx/fy) while every other node takes its
  // position FROM the sim, so the two never fight over ownership.
  const simRef = useRef<{ simulation: LiveSimulation; byId: Map<string, SimNode> } | null>(null)
  useEffect(() => {
    const simNodes: SimNode[] = layout.nodes.map((node) => {
      const radius = (node.width ?? 0) / 2
      return {
        id: node.id,
        radius,
        x: node.position.x + radius,
        y: node.position.y + radius,
      }
    })
    const simLinks: Array<SimulationLinkDatum<SimNode>> = layout.edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
    }))
    const byId = new Map(simNodes.map((node) => [node.id, node]))
    const simulation = createSimulation(simNodes, simLinks).alpha(0)
    simulation.on("tick", () => {
      setNodes((current) =>
        current.map((node) => {
          const sim = byId.get(node.id)
          if (!sim) return node
          if (node.dragging) {
            sim.fx = node.position.x + sim.radius
            sim.fy = node.position.y + sim.radius
            return node
          }
          return {
            ...node,
            position: { x: (sim.x ?? 0) - sim.radius, y: (sim.y ?? 0) - sim.radius },
          }
        })
      )
    })
    simRef.current = { simulation, byId }
    return () => {
      simulation.stop()
      simRef.current = null
    }
  }, [layout, setNodes])

  const handleNodeDragStart = useCallback((_event: unknown, node: Node) => {
    const live = simRef.current
    if (!live || prefersReducedMotion()) return
    const sim = live.byId.get(node.id)
    if (!sim) return
    sim.fx = node.position.x + sim.radius
    sim.fy = node.position.y + sim.radius
    live.simulation.alphaTarget(0.3).restart()
  }, [])

  const handleNodeDragStop = useCallback((_event: unknown, node: Node) => {
    const live = simRef.current
    if (!live) return
    const sim = live.byId.get(node.id)
    if (sim) {
      // Sync the sim to the drop point, then release the pin — the springs pull
      // the node toward equilibrium for the elastic settle.
      sim.x = node.position.x + sim.radius
      sim.y = node.position.y + sim.radius
      sim.fx = null
      sim.fy = null
    }
    live.simulation.alphaTarget(0)
  }, [])

  // Re-frame ONLY when the data changes (e.g. filters narrow the segments) — keyed
  // on `layout` alone, not on render, so a click-to-zoom isn't snapped back to the
  // overview by a stray re-fit. The short delay lets new nodes lay out first.
  useEffect(() => {
    const timer = window.setTimeout(() => void fitViewRef.current(FIT_OPTIONS), 60)
    return () => window.clearTimeout(timer)
  }, [layout])

  // Adjacency for hover highlighting (a node + its directly connected neighbours stay lit).
  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const edge of layout.edges) {
      ;(map.get(edge.source) ?? map.set(edge.source, new Set()).get(edge.source)!).add(edge.target)
      ;(map.get(edge.target) ?? map.set(edge.target, new Set()).get(edge.target)!).add(edge.source)
    }
    return map
  }, [layout.edges])

  const isLit = useCallback(
    (id: string) => !hovered || id === hovered || (adjacency.get(hovered)?.has(id) ?? false),
    [hovered, adjacency]
  )

  const displayNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        hidden: hiddenCompanyIds.has(node.id),
        data: {
          ...node.data,
          dimmed: !isLit(node.id),
          selected: node.id === `segment:${selectedSegmentId}`,
        },
      })),
    [nodes, isLit, selectedSegmentId, hiddenCompanyIds]
  )

  const displayEdges = useMemo(
    () =>
      layout.edges.map((edge) => {
        const lit = !hovered || edge.source === hovered || edge.target === hovered
        return {
          ...edge,
          hidden: hiddenCompanyIds.has(edge.source) || hiddenCompanyIds.has(edge.target),
          animated: hovered != null && lit,
          style: {
            stroke: lit && hovered ? "hsl(var(--primary))" : "hsl(var(--border))",
            strokeWidth: lit && hovered ? 2 : 1.4,
            opacity: edgeOpacity(lit, hovered != null),
          },
        }
      }),
    [layout.edges, hovered, hiddenCompanyIds]
  )

  const handleNodeClick = useCallback(
    (_event: unknown, node: Node) => {
      if (node.id.startsWith("segment:")) {
        onSelectSegment(node.id.replace("segment:", ""))
      } else if (node.id.startsWith("company:")) {
        const segmentId = layout.companySegment.get(node.id)
        if (segmentId) onOpenCompany((node.data as BubbleData).label, segmentId)
      }
    },
    [layout.companySegment, onSelectSegment, onOpenCompany]
  )

  // Expandable canvas (#2073 M-4): big portfolios get a taller viewport on demand.
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = () => {
    setExpanded((current) => !current)
    // Re-fit once the container has its new height.
    window.setTimeout(() => void fitViewRef.current(FIT_OPTIONS), 60)
  }

  return (
    <div
      className={cn(
        "border-border bg-card relative w-full overflow-hidden rounded-xl border shadow-sm transition-[height] duration-200",
        expanded ? "h-[85vh]" : "h-[640px]"
      )}
    >
      <ReactFlow
        fitView
        edges={displayEdges}
        edgesFocusable={false}
        fitViewOptions={FIT_OPTIONS}
        minZoom={0.1}
        nodes={displayNodes}
        nodesConnectable={false}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
        onNodeClick={handleNodeClick}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onNodeMouseEnter={(_event, node) => setHovered(node.id)}
        onNodeMouseLeave={() => setHovered(null)}
        onNodesChange={onNodesChange}
      >
        <Background className="text-border" gap={20} />
        <Controls className="!border-border !bg-background [&>button]:!border-border [&>button]:!bg-background [&>button]:!fill-foreground" />
      </ReactFlow>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-pressed={expanded}
            className="border-border bg-card/90 text-muted-foreground hover:text-foreground absolute right-3 bottom-3 size-8 border shadow-sm backdrop-blur"
            size="icon"
            variant="outline"
            onClick={toggleExpanded}
          >
            {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{expanded ? "Collapse map" : "Expand map"}</TooltipContent>
      </Tooltip>
      <ConnectionsGraphLegend
        hiddenRoles={hiddenRoles}
        marketFilter={effectiveMarketFilter}
        markets={markets}
        onToggleMarket={toggleMarket}
        onToggleRole={toggleRole}
        onReset={() => {
          setHiddenRoles(new Set())
          setMarketFilter(new Set())
        }}
      />
    </div>
  )
}

export function ConnectionsGraph({ segments, ...rest }: ConnectionsGraphProps) {
  if (segments.length === 0) {
    return (
      <div className="border-border bg-card text-muted-foreground grid h-[640px] w-full place-items-center rounded-xl border text-sm shadow-sm">
        No segments to map — adjust the filters.
      </div>
    )
  }
  return (
    <ReactFlowProvider>
      <TooltipProvider delayDuration={250}>
        <ConnectionsGraphInner segments={segments} {...rest} />
      </TooltipProvider>
    </ReactFlowProvider>
  )
}
